import { createContext, createElement, useContext, useEffect, useMemo, useState } from 'react';
import pl from './pl';
import en from './en';

export type Language = 'pl' | 'en';

export const languageLabels: Record<Language, string> = {
  pl: pl.language.polish,
  en: en.language.english,
};

type TranslationDictionary = Record<string, unknown>;

const dictionaries: Record<Language, TranslationDictionary> = {
  pl,
  en,
};

const STORAGE_KEY = 'assetflow.language';

function getValueAtPath(source: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((current, segment) => {
    if (current && typeof current === 'object' && segment in current) {
      return (current as Record<string, unknown>)[segment];
    }

    return undefined;
  }, source);
}

interface I18nContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
  dictionary: TranslationDictionary;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function getInitialLanguage(): Language {
  if (typeof window === 'undefined') {
    return 'pl';
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);

  return stored === 'en' ? 'en' : 'pl';
}

export function I18nProvider({ children }: { children: import('react').ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo<I18nContextValue>(() => {
    const dictionary = dictionaries[language];

    const setLanguage = (nextLanguage: Language) => {
      setLanguageState(nextLanguage);
    };

    const t = (key: string) => {
      const translation = getValueAtPath(dictionary, key);
      if (typeof translation === 'string') {
        return translation;
      }

      const fallback = getValueAtPath(dictionaries.pl, key);
      if (typeof fallback === 'string') {
        return fallback;
      }

      return key;
    };

    return {
      language,
      setLanguage,
      t,
      dictionary,
    };
  }, [language]);

  return createElement(I18nContext.Provider, { value }, children);
}

export function useI18n() {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }

  return context;
}

export function getLocalizedStatusLabel(language: Language, key: string) {
  const dictionary = dictionaries[language];
  const translation = getValueAtPath(dictionary, `common.status.${key}`);

  if (typeof translation === 'string') {
    return translation;
  }

  return key;
}

const statusValueMap: Record<string, string> = {
  Completed: 'completed',
  'Needs review': 'needsReview',
  Out: 'out',
  Available: 'available',
  'In Repair': 'inRepair',
  Reserved: 'reserved',
  Retired: 'retired',
  Active: 'active',
  'Pending review': 'pendingReview',
  Closed: 'closed',
  Busy: 'busy',
  Operational: 'operational',
  'Limited access': 'limitedAccess',
};

export function getLocalizedStatusLabelByValue(language: Language, value: string) {
  const normalizedKey = statusValueMap[value] ?? value;
  return getLocalizedStatusLabel(language, normalizedKey);
}

export function getLocalizedButtonLabel(language: Language, key: string) {
  const dictionary = dictionaries[language];
  const translation = getValueAtPath(dictionary, `common.buttons.${key}`);

  if (typeof translation === 'string') {
    return translation;
  }

  return key;
}
